import { Component } from 'react'
import GeneralSection from 'components/features/pfm/HomePage/GeneralSection'
import Anchor from 'components/atoms/navigation/Anchor'
import ExternalLayout from 'components/features/external/ExternalLayout/ExternalLayout'
import PressSection from 'components/features/pfm/HomePage/PressSection'
import SecuritySection from 'components/features/pfm/HomePage/SecuritySection'
import OverviewSection from 'components/features/pfm/HomePage/OverviewSection'
import HowitworksSection from 'components/features/pfm/HomePage/HowitworksSection'
import { Subject, merge, fromEvent } from 'rxjs'
import { connect } from 'react-redux'
import { StoreDispatch } from 'interfaces/component'
import { trackUIEventAction } from 'actions/analyticsActions'
import { AnalyticsEvent } from 'interfaces/analytics'
import { HomepageSection } from 'interfaces/homepage'
import { State } from 'interfaces/state'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import { Redirect } from 'react-router'
import { DASHBOARD_URL } from 'tangle-shared'
import { tap, skip, throttleTime, map, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators'
import Announcement from 'components/atoms/Announcement/Announcement'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'

type HomePageComponentStateProps = {
  isAuthenticated: boolean
}

type HomePageComponentDispatchProps = {
  trackUIEvent: (event: AnalyticsEvent) => void
}

type HomePageComponentProps =
  & HomePageComponentStateProps
  & HomePageComponentDispatchProps

class HomePageComponent extends Component<HomePageComponentProps> {
  private destroySubject = new Subject()
  private sections: Partial<Record<HomepageSection, HTMLElement>> = {}

  render () {
    const { isAuthenticated } = this.props
    if (isAuthenticated) {
      return (
        <Redirect to={ DASHBOARD_URL } />
      )
    }

    const announcement = (
      <Announcement>
        <h4>New:</h4> 2% Cash Back or Bitcoin Rewards on Every Purchase.
        <NavigationLink to='card'>Learn More</NavigationLink>
      </Announcement>
    )

    return (
      <Anchor placement='home'>
        <ExternalLayout
          type='homepage'
          announcementNode={ announcement }
        >
          <div ref={ this.handleRef('general') } />
          <GeneralSection />
          <div ref={ this.handleRef('press') } />
          <PressSection />
          <div ref={ this.handleRef('howitworks') } />
          <HowitworksSection />
          <div ref={ this.handleRef('overview') } />
          <OverviewSection />
          <div ref={ this.handleRef('security') } />
          <SecuritySection />
          <div ref={ this.handleRef('footer') } />
        </ExternalLayout>
      </Anchor>
    )
  }

  componentDidMount () {
    const { trackUIEvent } = this.props
    const getParams = this.getParams
    let pageParams: ReturnType<typeof getParams>

    merge(
      fromEvent(window, 'scroll').pipe(
        skip(2), // skip auto scroll on page load
        throttleTime(5),
        tap(() => {
          if (!pageParams) {
            pageParams = this.getParams()
          }
        }),
        map(() => pageParams),
        takeUntil(this.destroySubject),
      ),
      fromEvent(window, 'resize').pipe(
        debounceTime(500),
        tap(() => pageParams = this.getParams()),
        map(() => pageParams),
        takeUntil(this.destroySubject),
      )
    ).pipe(
      map((props) => ({
        ...props,
        scrollTop: window.scrollY,
      })),
      map(({ sectionsTop, scrollTop, viewportHeight }) => sectionsTop.find((section) => scrollTop > section.top - viewportHeight / 2)),
      distinctUntilChanged(),
      map((section) => section ? section.name : 'general'),
      tap((sectionName) => trackUIEvent({ category: 'page_controls-homepage', action: 'scroll_to', label: sectionName })),
      takeUntil(this.destroySubject),
    )
      .subscribe()
  }

  componentWillUnmount () {
    this.destroySubject.next()
  }

  private getParams () {
    return {
      viewportHeight: window.innerHeight,
      sectionsTop: Object.keys(this.sections)
        .map((name: HomepageSection) => ({
          top: this.sections[name]!.offsetTop,
          name,
        }))
        .sort((a, b) => b.top - a.top),
    }
  }

  private handleRef = (name: HomepageSection) => (element: HTMLElement | null) => {
    if (!element) {
      delete this.sections[name]
    } else {
      this.sections[name] = element
    }

  }
}

const mapStateToProps = (state: State): HomePageComponentStateProps => {
  return {
    isAuthenticated: !!isAuthenticatedSelector(state),
  }
}

const mapDispatchToProps = (dispatch: StoreDispatch): HomePageComponentDispatchProps => ({
  trackUIEvent: (event) => dispatch(trackUIEventAction(event)),
})

const HomePage = connect<HomePageComponentStateProps, HomePageComponentDispatchProps>(
  mapStateToProps, mapDispatchToProps
)(HomePageComponent)

export default HomePage
